// Inline sensitive asks (store change `conversational-forms-inline-sensitive-asks`,
// slice 1: radio and text) — the field's own input control, rendered under the
// bot message that asked for it.
//
// THE PRIVACY PROPERTY. The value a visitor sets here is a form-view value and
// nothing else: it goes to forms state `values` through `commitInlineValue`
// (`setValue`, which also confirms the id), exactly like a form-page edit. It
// is NEVER written into a `ChatMessage` (the persisted chat history, sent as
// `context` on every turn), into `message.fills`, or into any request body.
// The hidden "saved" marker turn carries the field's id only. This component
// DISPLAYS the value, reading it from `state.values` at render time — it keeps
// no copy beyond a text control's unsaved draft, which is component-local.
//
// Live vs collapsed. The control is live only while that message's
// `inlineAskFieldId` is the server's current inline ask (`liveInlineField`) and
// this is the latest message carrying that id. Otherwise it collapses to a
// one-line row: the current local value (latest row only — older rows for the
// same field read "saved", so the value appears once in the thread) or
// "not filled", with a way into the form view.

import { FormEvent, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../App/AppContext';
import { ChatMessage } from '../Shared/API/ApiTypes';
import { INLINE_SKIP_LABEL } from './constants';
import styles from './ConversationalForms.module.css';
import { FieldInput } from './FieldControls';
import { useConversationalForms } from './FormsContext';
import { CfField, CfForm, FieldValue } from './types';
import { useLiveInlineField } from './useLiveInlineField';
import { validateFormat } from './validation';
import { displayValue, isEmptyValue, normalize } from './valueFormat';

const InlineFieldAsk = ({ fieldId, message }: { fieldId: string; message: ChatMessage }) => {
    const {
        active,
        registry,
        state,
        commitInlineValue,
        skipInline,
        openFormView,
        openReview,
    } = useConversationalForms();
    const { state: appState } = useContext(AppContext);
    const liveField = useLiveInlineField();

    // Unsaved text for a text control. Local on purpose: not form state, not a
    // message, not persisted. Cleared when the value is committed.
    const [draft, setDraft] = useState('');
    const [error, setError] = useState<string | null>(null);
    const groupRef = useRef<HTMLDivElement>(null);

    const field: CfField | undefined = registry?.fieldsById.get(fieldId);
    const form: CfForm | undefined = registry?.forms.find((f) => f.fields.some((ff) => ff.id === fieldId));

    // Only the latest message carrying this field's id may be live or show the
    // value. A message the history does not contain (a stand-alone render)
    // counts as the latest.
    const sameField = (appState.messages ?? []).filter((m) => m.inlineAskFieldId === fieldId);
    const latest = sameField[sameField.length - 1];
    const isLatest = !latest || latest === message;
    const live = Boolean(field) && liveField?.id === fieldId && isLatest;

    // The control appears without the visitor moving (the composer locks at the
    // same moment): take focus to its group container so it is announced and
    // Tab reaches the options, Save and Skip. Never an option (a radio saves on
    // click) or the input (it could take keystrokes meant for the chat). Runs
    // whenever the control renders live, mount included.
    useEffect(() => {
        if (live) groupRef.current?.focus();
    }, [live]);

    if (!active || !field || !form) return null;

    // An older row for a field whose newest row is live shows nothing: a
    // "not filled" line right above the open control would contradict it.
    if (!isLatest && liveField?.id === fieldId) return null;

    if (live) {
        const save = () => {
            const text = draft.trim();
            if (!text) return;
            const value = normalize(field, text);
            const problem = validateFormat(field, value);
            if (problem) {
                setError(problem);
                return;
            }
            setError(null);
            setDraft('');
            commitInlineValue(field.id, value);
        };
        const onSubmit = (e: FormEvent) => {
            e.preventDefault();
            save();
        };
        return (
            <div
                className={styles.inlineAsk}
                role="group"
                aria-label={field.label}
                tabIndex={-1}
                ref={groupRef}
                data-testid="cf-inline-ask"
            >
                {field.type === 'radio' ? (
                    <FieldInput
                        field={field}
                        value={undefined}
                        onChange={(value: FieldValue) => commitInlineValue(field.id, value)}
                    />
                ) : (
                    <form className={styles.inlineAskForm} onSubmit={onSubmit} noValidate>
                        <FieldInput
                            field={field}
                            value={draft}
                            onChange={(value: FieldValue) => {
                                setDraft(String(value));
                                if (error) setError(null);
                            }}
                        />
                        {error && (
                            <div className={styles.fieldError} role="alert">
                                {error}
                            </div>
                        )}
                        <button type="submit" className={styles.inlineSave}>
                            Save
                        </button>
                    </form>
                )}
                {field.help ? (
                    <div className={styles.fieldHelp} id={`${field.id}-help`}>
                        {field.help}
                    </div>
                ) : null}
                <button
                    type="button"
                    className={styles.inlineLink}
                    onClick={() => skipInline(field.id)}
                >
                    {INLINE_SKIP_LABEL}
                </button>
            </div>
        );
    }

    // Collapsed. The value, when shown, is read from local forms state right
    // here — never from the message.
    const value = state.values[field.id];
    const hasValue = !isEmptyValue(value);
    const formId = form.objectId;
    const discarded = state.discardedFormIds.includes(formId);
    const submitted = state.submittedFormIds.includes(formId);
    const isActiveForm = state.activeFormId === formId;
    // A paused or queued form has no form view to open from here; its own
    // resume rows are the way back.
    const openAction = (() => {
        if (discarded) return null;
        if (submitted) return () => openReview(formId);
        if (isActiveForm) return openFormView;
        return null;
    })();

    if (hasValue) {
        const shown = isLatest ? displayValue(field, value) : 'saved';
        return (
            <div className={styles.inlineRow} data-testid="cf-inline-row">
                <span className={styles.inlineRowText}>{`${field.label} · ${shown}`}</span>
                {isLatest && openAction && (
                    <button
                        type="button"
                        className={styles.inlineLink}
                        onClick={openAction}
                        aria-label={`Change ${field.label}`}
                    >
                        Change
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className={styles.inlineRow} data-testid="cf-inline-row">
            <span className={styles.inlineRowText}>
                {`${field.label} · not filled — you can do it in the form`}
            </span>
            {openAction && (
                <button
                    type="button"
                    className={styles.inlineLink}
                    onClick={openAction}
                    aria-label={`Open form to fill ${field.label}`}
                >
                    Open form
                </button>
            )}
        </div>
    );
};

export default InlineFieldAsk;
